/**
 * sl-9qm.4.3: FilterBar Component
 *
 * Filter UI with dropdown, date-range, and text-search inputs.
 * Config: filterableColumns[], showResetButton.
 */
import { createElement, useState } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  ViewComponent,
  ViewComponentRenderProps,
} from '../../../types/view-component.types';
import * as styles from '../styles.css';

const filterColumnSchema = z.object({
  field: z.string(),
  label: z.string(),
  type: z.enum(['text', 'select', 'date-range']),
  options: z.array(z.string()).optional(),
});

const configSchema = z.object({
  display: z.enum(['controls', 'chips']).default('controls'),
  filterableColumns: z.array(filterColumnSchema).default([]),
  showResetButton: z.boolean().default(true),
});

type FilterColumn = z.infer<typeof filterColumnSchema>;

function FilterBarRenderer({
  config,
  data,
  runtimeContext,
}: ViewComponentRenderProps) {
  const { display, filterableColumns, showResetButton } = config as z.infer<
    typeof configSchema
  >;
  const [filters, setFilters] = useState<Record<string, string>>({});

  const updateFilter = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => setFilters({});

  if (display === 'chips') {
    const column = filterableColumns[0] ?? {
      field: 'tags',
      label: 'Categories',
      type: 'select' as const,
    };
    const active = filters[column.field] ?? '';
    const allLabel = runtimeContext?.locale?.startsWith('de') ? 'Alle' : 'All';
    const options = resolveChipOptions(column, data);

    return createElement(
      'div',
      {
        role: 'group',
        'aria-label': column.label,
        className: styles.filterBarChips,
        'data-sl-role': 'category-filter',
      },
      createElement('span', { className: styles.filterBarLabel }, column.label),
      createElement(
        'div',
        { className: styles.filterChipList },
        createElement(
          'button',
          {
            type: 'button',
            className: styles.filterChip,
            'data-active': active === '' ? 'true' : 'false',
            onClick: () => updateFilter(column.field, ''),
          },
          allLabel
        ),
        ...options.map(option =>
          createElement(
            'button',
            {
              key: option.value,
              type: 'button',
              className: styles.filterChip,
              'data-active': active === option.value ? 'true' : 'false',
              onClick: () => updateFilter(column.field, option.value),
            },
            option.label
          )
        )
      )
    );
  }

  const renderFilterInput = (col: FilterColumn) => {
    switch (col.type) {
      case 'select':
        return createElement(
          'select',
          {
            key: col.field,
            value: filters[col.field] ?? '',
            onChange: (e: React.ChangeEvent<HTMLSelectElement>) =>
              updateFilter(col.field, e.target.value),
            'aria-label': `Filter by ${col.label}`,
            style: {
              padding:
                'var(--sl-custom-app-gap-xs) var(--sl-custom-app-padding-sm)',
              border: '1px solid var(--streamlain-border-color)',
              borderRadius: 'var(--sl-custom-app-radius-sm)',
              background: 'var(--streamlain-background-primary-color)',
              fontSize: 'var(--sl-custom-app-font-size-sm)',
            },
          },
          createElement('option', { value: '' }, `All ${col.label}`),
          ...(col.options ?? []).map(opt =>
            createElement('option', { key: opt, value: opt }, opt)
          )
        );
      case 'date-range':
        return createElement(
          'div',
          {
            key: col.field,
            style: {
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sl-custom-app-gap-xs)',
            },
          },
          createElement('input', {
            type: 'date',
            value: filters[`${col.field}_from`] ?? '',
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              updateFilter(`${col.field}_from`, e.target.value),
            'aria-label': `${col.label} from`,
            style: {
              padding:
                'var(--sl-custom-app-gap-xxs) var(--sl-custom-app-gap-xs)',
              border: '1px solid var(--streamlain-border-color)',
              borderRadius: 'var(--sl-custom-app-radius-sm)',
              fontSize: 'var(--sl-custom-app-font-size-sm)',
            },
          }),
          createElement(
            'span',
            { style: { color: 'var(--streamlain-text-disable-color)' } },
            '–'
          ),
          createElement('input', {
            type: 'date',
            value: filters[`${col.field}_to`] ?? '',
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              updateFilter(`${col.field}_to`, e.target.value),
            'aria-label': `${col.label} to`,
            style: {
              padding:
                'var(--sl-custom-app-gap-xxs) var(--sl-custom-app-gap-xs)',
              border: '1px solid var(--streamlain-border-color)',
              borderRadius: 'var(--sl-custom-app-radius-sm)',
              fontSize: 'var(--sl-custom-app-font-size-sm)',
            },
          })
        );
      default:
        return createElement('input', {
          key: col.field,
          type: 'text',
          placeholder: col.label,
          value: filters[col.field] ?? '',
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            updateFilter(col.field, e.target.value),
          'aria-label': `Filter by ${col.label}`,
          style: {
            padding:
              'var(--sl-custom-app-gap-xs) var(--sl-custom-app-padding-sm)',
            border: '1px solid var(--streamlain-border-color)',
            borderRadius: 'var(--sl-custom-app-radius-sm)',
            fontSize: 'var(--sl-custom-app-font-size-sm)',
            minWidth: 'var(--sl-custom-app-filter-min-width)',
          },
        });
    }
  };

  return createElement(
    'div',
    {
      role: 'search',
      'aria-label': 'Filter bar',
      className: styles.filterBar,
      'data-sl-role': 'filter-bar',
    },
    ...filterableColumns.map(renderFilterInput),
    showResetButton &&
      filterableColumns.length > 0 &&
      createElement(
        'button',
        {
          onClick: resetFilters,
          style: {
            padding:
              'var(--sl-custom-app-gap-xs) var(--sl-custom-app-padding-md)',
            border: '1px solid var(--streamlain-border-color)',
            borderRadius: 'var(--sl-custom-app-radius-sm)',
            background: 'var(--streamlain-background-primary-color)',
            cursor: 'pointer',
            fontSize: 'var(--sl-custom-app-font-size-sm)',
            color: 'var(--streamlain-text-secondary-color)',
          },
          'aria-label': 'Reset all filters',
        },
        'Reset'
      )
  );
}

function resolveChipOptions(
  column: FilterColumn,
  data: ViewComponentRenderProps['data']
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  const options: Array<{ value: string; label: string }> = [];

  const push = (raw: unknown) => {
    const value = normalizeOptionValue(raw);
    if (!value || seen.has(value)) return;
    seen.add(value);
    options.push({ value, label: formatOptionLabel(value) });
  };

  for (const option of column.options ?? []) {
    push(option);
  }
  for (const row of data) {
    push(row[column.field]);
  }

  return options;
}

function normalizeOptionValue(raw: unknown): string | null {
  if (Array.isArray(raw)) {
    return raw
      .map(item => normalizeOptionValue(item))
      .filter((value): value is string => Boolean(value))
      .join(', ');
  }
  if (raw && typeof raw === 'object') {
    const record = raw as Record<string, unknown>;
    return normalizeOptionValue(
      record['name'] ?? record['title'] ?? record['label'] ?? record['id']
    );
  }
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed ? trimmed : null;
}

function formatOptionLabel(value: string): string {
  return value
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, match => match.toUpperCase());
}

export const FilterBarComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.FILTER_BAR,
  type: 'navigation',
  name: 'Filter Bar',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    '⧩'
  ),
  capabilities: [],
  configSchema,
  defaultConfig: {
    display: 'controls',
    filterableColumns: [],
    showResetButton: true,
  },
  render(props) {
    return createElement(FilterBarRenderer, props);
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      '⧩ Filter Bar'
    );
  },
};
